/**
 * Sidebar footer identity row, 56px. Round avatar — it's a person, unlike
 * the squared workspace avatar. Trailing DotsVertical opens AccountMenu
 * upward from the footer. Impersonating adds a "Viewing as" eyebrow (in
 * place of the email line) and a warning-200 ring on the avatar — redundant
 * with the impersonation bar on purpose, since the bar can scroll out of
 * view on a long page and the footer never does.
 * Spec: assets/handoffs/design_handoff_app_shell, Rules — shell /
 * impersonation.
 *
 * @param {{name,email,initials}} account
 * @param {boolean=false} collapsed
 * @param {boolean=false} impersonating
 * @param {ReactElement} menu - AccountMenu, rendered when open
 */
import React from "react";
import classNames from "classnames";
import Avatar from "../../../theme/untitled/Avatar/Avatar";
import Icon from "../../../theme/untitled/icons/Icon";
import { useDismissablePanel } from "../useDismissablePanel";
import styles from "./AccountRow.module.scss";

const AccountRow = ({ account, collapsed = false, impersonating = false, renderMenu }) => {
  const { open, setOpen, triggerRef, panelRef, close } = useDismissablePanel();

  const avatar = (
    <span className={classNames(styles.avatarWrap, { [styles["avatarWrap--impersonating"]]: impersonating })}>
      <Avatar size="sm" initials={account?.initials} />
    </span>
  );

  if (collapsed) {
    return (
      <div className={styles.collapsedWrap}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.collapsedTrigger}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {avatar}
        </button>
        {open && (
          <div ref={panelRef} className={styles.flyoutPanel}>
            {renderMenu(close)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={classNames(styles.row, { [styles["row--open"]]: open })}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {avatar}
        <span className={styles.text}>
          {impersonating && <span className={styles.eyebrow}>Viewing as</span>}
          <span className={styles.name}>{account?.name}</span>
          {!impersonating && <span className={styles.email}>{account?.email}</span>}
        </span>
        <Icon name="DotsVertical" className={styles.dots} />
      </button>
      {open && (
        <div ref={panelRef} className={styles.panel}>
          {renderMenu(close)}
        </div>
      )}
    </div>
  );
};

export default AccountRow;
